import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import DataSource from 'devextreme/data/data_source';
import { DxOdataStoreService, DxPermissionGuardConfig } from '@shared/components/dx-data-grid/data-access';
import {
  IClosedContract,
  IContract,
  IContractFileHistoryItem,
  IContractModel,
  IContractOdata,
  IDataMockup,
  ISendToSignResponse,
  ISendToSignsNotSendsResponse,
  ISurety
} from '../entities/contracts.interface';
import { IContractNotSentResult } from '../entities/contracts.model';
import { APP_CONFIG } from '@application/config';
import { TResponseValidation } from '@treino/data-access';

@Injectable({providedIn: 'root'})
export class ContractsService {
  private readonly dxOdataService = inject(DxOdataStoreService);
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  public getTemplateModel(): Observable<string> {
    return this.http.post(
      this.apiUrl.contract + `/Models/GetTemplateContent`,
      {},
      {
        withCredentials: true,
        responseType: 'text',
      }
    );
  }

  public getModelById(
    id: IContractModel['ModelId']
  ): Observable<IContractModel> {
    return this.http.get<IContractModel>(
      this.apiUrl.contract + `/Models?id=${id}`,
      {
        withCredentials: true,
      }
    );
  }

  public getDataMockup(): Observable<IDataMockup> {
    return this.http.post<IDataMockup>(
      this.apiUrl.contract + `/Models/GetDataMockup`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  public createModel(data: any): Observable<IContractModel> {
    return this.http.post<IContractModel>(
      this.apiUrl.contract + `/Models`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  public updateModel(data: any): Observable<IContractModel> {
    return this.http.put<IContractModel>(
      this.apiUrl.contract + `/Models`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  public deleteModel(data: IContractModel['ModelId']): Observable<IContractModel> {
    return this.http.delete<IContractModel>(
      this.apiUrl.contract + `/Models?id=${data}`,
      {
        withCredentials: true,
      }
    );
  }

  public getModelsFromOdata(params?: any): DataSource<IContractModel[]> {
    return this.dxOdataService.fromOdata<IContractModel[]>({
      url: this.apiUrl.odata_contract,
      metadata: 'Models',
      key: 'ModelId',
      params
    });
  }

  public getContractsFromOdata(params?: any, permissionGuard?: DxPermissionGuardConfig): DataSource<IContract[]> {
    return this.dxOdataService.fromOdata<IContract[]>({
      url: this.apiUrl.odata_contract,
      metadata: 'Contracts',
      key: 'ContractId',
      params,
      permissionGuard,
      exposeRequest: true,
    });
  }

  public getCount(filter?: string): Observable<number> {
    const params: any = { $count: true, $top: 0 };
    if (filter) params.$filter = filter;

    return this.http.get<any>(`${this.apiUrl.odata_contract}/Contracts`, {
      params,
      withCredentials: true,
    }).pipe(
      map(response => response['@odata.count'])
    );
  }

  public getContractsModelSummary(filter?: string): Observable<{ ModelId: string; ModelDescription: string }[]> {
    const params: any = { $select: 'ModelId,ModelDescription' };
    if (filter) params.$filter = filter;

    return this.http.get<any>(`${this.apiUrl.odata_contract}/Contracts`, {
      params,
      withCredentials: true,
    }).pipe(
      map(response => response.value || [])
    );
  }

  /**
   * Lists every contract blocked from sending (status 'added' with a non-empty
   * `ErrorMessage`) — the same filter that feeds the `addedWithError` count, but
   * without `$top`, so it returns all blocked contracts for the quick-fix flow.
   */
  public getBlockedContracts(): Observable<IContractOdata[]> {
    const params: any = {
      $filter: "Status eq 'added' and ErrorMessage ne null and ErrorMessage ne ''",
      $select: 'ContractId,PersonId,WitnessOnePersonId,WitnessTwoPersonId,SuretyPersonId,SignerFullName,WitnessOneSignerFullName,WitnessTwoSignerFullName,SuretyFullName,SignerPhoneNumber,WitnessOneSignerPhoneNumber,WitnessTwoSignerPhoneNumber,SuretyPhoneNumber,Description,ModelDescription,StartedOn,ErrorMessage',
      $orderby: 'StartedOn desc',
    };

    return this.http.get<any>(`${this.apiUrl.odata_contract}/Contracts`, {
      params,
      withCredentials: true,
    }).pipe(
      map(response => response.value || [])
    );
  }

  public getSuretiesFromOdata(params?: any): DataSource<ISurety[]> {
    return this.dxOdataService.fromOdata<ISurety[]>({
      url: this.apiUrl.odata_contract,
      metadata: 'Sureties',
      key: 'SuretyPersonId',
      params
    });
  }

  public getCreditorsFromOdata(params?: any): DataSource {
    return this.dxOdataService.fromOdata({
      url: this.apiUrl.odata_contract,
      metadata: 'Creditors',
      params
    });
  }

  public getContract(contratoId: string): Observable<IContract> {
    return this.http.get<IContract>(
      this.apiUrl.contract + `/Contracts?id=${contratoId}`,
      {
        withCredentials: true,
      }
    );
  }

  public getFileHistory(
    contractId: string
  ): Observable<IContractFileHistoryItem[]> {
    return this.http.get<{ data: IContractFileHistoryItem[] }>(
      this.apiUrl.contract + `/ContractsSign/GetFileHistory`,
      {
        params: { contractId },
        withCredentials: true,
      }
    ).pipe(map((response) => response?.data ?? []));
  }

  public getPDFByUrl(url: string): Observable<Blob> {
    return this.http.get(`${url}`, {
      withCredentials: false,
      responseType: 'blob',
    });
  }

  public addContract(data: Partial<IContract>): Observable<IContract> {
    return this.http.post<IContract>(
      this.apiUrl.contract + `/Contracts`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  public updateContract(data: IContract): Observable<IContract> {
    return this.http.put<IContract>(
      this.apiUrl.contract + `/Contracts`,
      data,
      {
        withCredentials: true,
      }
    );
  }

  public deleteContract(id: IContract['ContractId']): Observable<IContract> {
    return this.http.delete<IContract>(
      this.apiUrl.contract + `/Contracts?id=${id}`,
      {
        withCredentials: true,
      }
    );
  }

  public sendToSign(
    contractId: IContract['ContractId']
  ): Observable<ISendToSignResponse> {
    return this.http.post<ISendToSignResponse>(
      this.apiUrl.contract + `/ContractsSign/SendToSign?contractId=${contractId}`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  public sendMailNotification(contractId: string): Observable<TResponseValidation> {
    return this.http.post<TResponseValidation>(
      this.apiUrl.contract + `/Notifications/SendMailNotification`,
      {},
      {
        params: { contractId },
        withCredentials: true,
      }
    );
  }

  public sendToSignsNotSends(): Observable<ISendToSignsNotSendsResponse> {
    return this.http.post<ISendToSignsNotSendsResponse>(
      this.apiUrl.contract + `/ContractsSign/SendToSignsNotSents`,
      {},
      { withCredentials: true }
    );
  }

  public sendToSignsPending(): Observable<ISendToSignsNotSendsResponse> {
    return this.http.post<ISendToSignsNotSendsResponse>(
      this.apiUrl.contract + `/ContractsSign/SendToSignsPending`,
      {},
      { withCredentials: true }
    );
  }

  public getResultsContractsNotSents(eventId: string): Observable<IContractNotSentResult[]> {
    return this.http.get<IContractNotSentResult[]>(this.apiUrl.contract + `/ContractsSign/GetResultsContractsNotSents`, {
      params: { eventId },
      withCredentials: true,
    });
  }

  public sendRemindersUnsignedContracts(): Observable<TResponseValidation> {
    return this.http.post<TResponseValidation>(
      this.apiUrl.contract + `/ContractsSign/SendRemindersUnsignedContracts`,
      {},
      { withCredentials: true }
    );
  }

  public getTemplateModelFile(ModelId: string): Observable<string> {
    return this.http.get<string>(
      this.apiUrl.contract + `/Models/GetTemplateFile`,
      {
        params: { ModelId },
        withCredentials: true,
        responseType: 'text' as 'json',
      }
    )
  }

  public closeContract(payload: IClosedContract): Observable<IContract> {
    return this.http.put<IContract>(
      this.apiUrl.contract + `/Contracts/CloseContract`,
      payload,
      {
        withCredentials: true,
      }
    );
  }
}
